// Renders a switchable line chart from a Rails-prepared JSON payload without navigation.
// The payload carries all content and formatting, so this controller stays generic:
//   { "labels": [ "Jul 1", ... ],
//     "metrics": { "<key>": { "label", "chartLabel", "caption", "format", "prefix",
//                             "precision", "values": [...], "formatted": [ ... ] } } }
// `values` are the plottable coordinates; `formatted` are the final backend-rendered
// display strings used verbatim for tooltips and the no-JavaScript fallback table.
// Usage:
//   <section data-controller="metric-line-chart" data-metric-line-chart-metric-value="spend"
//            data-metric-line-chart-color-value="#6fd35f">
//     <a href="..." data-metric="clicks" data-action="metric-line-chart#switch"
//        data-metric-line-chart-target="control">Clicks</a>
//     <script type="application/json" data-metric-line-chart-target="data">{ ... }</script>
//     <canvas data-metric-line-chart-target="canvas"></canvas>
//     <p data-metric-line-chart-target="tooltip"></p>
//     <table data-metric-line-chart-target="fallback">
//       <caption data-metric-line-chart-target="fallbackCaption"></caption>
//       <th data-metric-line-chart-target="fallbackHeading"></th>
//       <td data-metric-line-chart-target="fallbackValue"></td>
//     </table>
//   </section>
import { Controller } from "stimulus"
import Chart from "chart.js/auto"

export default class extends Controller {
  static targets = [ "canvas", "control", "data", "fallback", "fallbackCaption", "fallbackHeading", "fallbackValue", "tooltip" ]
  static values = { metric: String, color: String }

  connect() {
    this.series = JSON.parse(this.dataTarget.textContent)
    this.fallbackTarget.classList.add("is-enhanced")
    this.render()
  }

  disconnect() {
    this.destroyChart()
    this.fallbackTarget.classList.remove("is-enhanced")
  }

  switch(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    event.preventDefault()
    const control = event.currentTarget
    this.metricValue = control.dataset.metric
    this.updateControls(control)
    this.updateUrl(control.href)
    this.render()
  }

  render() {
    this.destroyChart()
    const metric = this.currentMetric()
    this.canvasTarget.setAttribute("aria-label", metric.chartLabel)
    this.canvasTarget.dataset.tooltipFormat = metric.format
    this.fallbackCaptionTarget.textContent = metric.caption
    this.fallbackHeadingTarget.textContent = metric.label
    this.fallbackValueTargets.forEach((cell, index) => {
      cell.textContent = metric.formatted[index]
    })
    const context = this.canvasTarget.getContext("2d")

    // A one-day range cannot draw a line, so the lone point renders centered
    // as a small always-visible ringed dot; hover grows it and shows the
    // tooltip exactly like the multi-day chart's points.
    const singleDay = this.series.labels.length === 1

    this.chart = new Chart(context, {
      type: "line",
      data: {
        labels: this.series.labels,
        datasets: [ {
          label: metric.label,
          // Chart.js may parse decimal strings for coordinates, but display paths
          // use the backend-formatted string at the same index, never the float.
          data: metric.values,
          borderColor: this.color,
          borderWidth: 2,
          backgroundColor: this.fillGradient(context),
          fill: !singleDay,
          tension: 0.4,
          // The lone point rests small and grows to the shared hover size (6)
          // on mouseover, exactly like the multi-day chart's points.
          pointRadius: singleDay ? 3.5 : 0,
          pointBackgroundColor: "#ffffff",
          pointBorderColor: this.color,
          pointBorderWidth: singleDay ? 2 : 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: "#ffffff",
          pointHoverBorderColor: this.color,
          pointHoverBorderWidth: 2
        } ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1b1c1e",
            padding: 12,
            cornerRadius: 10,
            displayColors: false,
            callbacks: { label: (context) => metric.formatted[context.dataIndex] },
            external: (context) => this.updateAccessibleTooltip(context, metric)
          }
        },
        interaction: { mode: "index", intersect: false },
        // Horizontal guides only: the x grid and the y axis border are hidden so
        // the plot reads as faint hairlines under the curve, like the prototype.
        scales: {
          x: {
            // Offsetting the lone category centers the focus point in the plot
            // instead of pinning it against the y axis.
            offset: singleDay,
            grid: { display: false },
            ticks: { color: "#8b909a", maxTicksLimit: 10, maxRotation: 0 }
          },
          y: {
            beginAtZero: true,
            border: { display: false },
            grid: { color: "#e8edf3" },
            ticks: { color: "#8b909a", maxTicksLimit: 6, callback: (value) => this.formatTick(value, metric) }
          }
        }
      }
    })
  }

  updateControls(activeControl) {
    this.controlTargets.forEach((control) => {
      const active = control === activeControl
      control.classList.toggle("is-active", active)
      if (active) {
        control.setAttribute("aria-current", "page")
      } else {
        control.removeAttribute("aria-current")
      }
    })
  }

  updateUrl(href) {
    const replacement = new URL(href, window.location.origin)
    window.history.replaceState(window.history.state, "", `${replacement.pathname}${replacement.search}`)
  }

  // The selected metric's payload; an unknown key falls back to the first metric
  // so a stale or tampered value attribute never breaks the chart.
  currentMetric() {
    return this.series.metrics[this.metricValue] || Object.values(this.series.metrics)[0]
  }

  // Axis ticks are Chart.js-chosen round numbers, not data points, so they cannot
  // arrive pre-formatted; the metric's prefix and precision keep them visually
  // consistent with the backend-formatted point values.
  formatTick(value, metric) {
    const number = Number(value).toLocaleString("en-US", {
      minimumFractionDigits: metric.precision,
      maximumFractionDigits: metric.precision
    })
    return `${metric.prefix}${number}`
  }

  updateAccessibleTooltip(context, metric) {
    if (context.tooltip.opacity === 0 || context.tooltip.dataPoints.length === 0) {
      this.tooltipTarget.textContent = ""
    } else {
      const point = context.tooltip.dataPoints[0]
      this.tooltipTarget.textContent = `${point.label}: ${metric.formatted[point.dataIndex]}`
    }
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  // The stroke color for the line and point rings; defaults to the green used
  // by the first adopter so existing markup keeps its accepted look.
  get color() {
    return this.hasColorValue && this.colorValue !== "" ? this.colorValue : "#6fd35f"
  }

  // The area fill fades from the line color to transparent, matching the
  // accepted prototype's gradient rather than a flat translucent block.
  fillGradient(context) {
    const gradient = context.createLinearGradient(0, 0, 0, this.canvasTarget.parentNode.clientHeight || 300)
    gradient.addColorStop(0, this.rgba(this.color, 0.35))
    gradient.addColorStop(1, this.rgba(this.color, 0))
    return gradient
  }

  // Expands a 6-digit hex color into an rgba() string with the given alpha.
  rgba(hex, alpha) {
    const value = parseInt(hex.replace("#", ""), 16)
    return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`
  }
}
